import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { fetchEngagementDealsAndResell, fetchClients } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

import EngagementCards from "./EngagementCards";
import ResellCards from "./ResellCards";
import AnalyticsTab from "./AnalyticsTab";
import { FaHandshake } from "react-icons/fa";

/* -------------------------------- Animations ------------------------------- */

const contentAnim = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

/* ------------------------------ Helpers ----------------------------------- */

const formatLastActivity = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

/* ----------------------------- Summary card ------------------------------- */

const SummaryCard = ({ eyebrow, value, subtitle, accent }) => (
  <div
    style={{
      flex: 1,
      minWidth: 180,
      background: "var(--color-bg-card)",
      border: "1px solid var(--color-border)",
      borderRadius: 12,
      padding: "16px 18px 14px",
      position: "relative",
      boxShadow: "var(--shadow-sm)",
      overflow: "hidden",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: accent,
      }}
    />
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--color-text-light)",
        marginBottom: 8,
      }}
    >
      {eyebrow}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: "var(--color-text)",
        lineHeight: 1.1,
        marginBottom: 6,
        wordBreak: "break-word",
      }}
    >
      {value}
    </div>
    {subtitle && (
      <div
        style={{
          fontSize: 12,
          color: "var(--color-text-light)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {subtitle.dot && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: subtitle.dot,
              display: "inline-block",
            }}
          />
        )}
        <span>{subtitle.text}</span>
      </div>
    )}
  </div>
);

/* -------------------------------------------------------------------------- */

const EngagementPage = ({ clientId, defaultTab = "engagement", selectedClient }) => {
  const [tab, setTab] = useState(defaultTab);
  const { getCachedData, setCachedData } = useDataCache();
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
  const [clientName, setClientName] = useState("");

  const [engagementDeals, setEngagementDeals] = useState([]);
  const [resellDeals, setResellDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  // Aggregated PM Status across all engagement deals (filled by EngagementCards
  // via callback so we do not duplicate API calls).
  const [pmStats, setPmStats] = useState({});

  const dataLoadedRef = useRef(new Set());
  const currentClientRef = useRef(clientId);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* --------------------------- Client name --------------------------- */

  useEffect(() => {
    if (!clientId) {
      setClientName("");
      return;
    }

    if (selectedClient?.client_name) {
      setClientName(selectedClient.client_name);
      return;
    }

    const cacheKey = `client_name_${clientId}`;
    const cachedName = getCachedData(cacheKey);
    if (cachedName) {
      setClientName(cachedName);
      return;
    }

    const fetchClientName = async () => {
      try {
        const clientsList = await fetchClients();
        const foundClient = clientsList.find((c) => c.client_id === parseInt(clientId));
        if (foundClient?.client_name) {
          setClientName(foundClient.client_name);
          setCachedData(cacheKey, foundClient.client_name);
        } else {
          setClientName("");
        }
      } catch (error) {
        console.error("Error fetching client name:", error);
        setClientName("");
      }
    };

    fetchClientName();
  }, [clientId, selectedClient, getCachedData, setCachedData]);

  /* ------------------------------ Data Fetch ------------------------------ */

  useEffect(() => {
    if (currentClientRef.current !== clientId) {
      dataLoadedRef.current.clear();
      currentClientRef.current = clientId;
      setPmStats({});
    }

    if (!clientId) {
      setEngagementDeals([]);
      setResellDeals([]);
      setLoading(false);
      return;
    }

    const cacheKey = `engagement_${clientId}`;
    const cachedEngagement = getCachedData("engagement_deals", clientId);
    const cachedResell = getCachedData("resell_deals", clientId);
    const hasFreshCache = cachedEngagement !== undefined && cachedResell !== undefined;

    // Only short-circuit while the underlying cache entry is still fresh --
    // getCachedData returns undefined once its TTL elapses, so this naturally
    // falls through to a refetch instead of serving stale data for the whole session.
    if (dataLoadedRef.current.has(cacheKey) && hasFreshCache) {
      return;
    }

    if (hasFreshCache) {
      setEngagementDeals(Array.isArray(cachedEngagement) ? cachedEngagement : []);
      setResellDeals(Array.isArray(cachedResell) ? cachedResell : []);
      setLoading(false);
      dataLoadedRef.current.add(cacheKey);
      return;
    }

    setLoading(true);
    dataLoadedRef.current.add(cacheKey);

    fetchEngagementDealsAndResell(clientId)
      .then(({ deals: engagement, resell_deals: resell }) => {
        setCachedData("engagement_deals", engagement, clientId);
        setCachedData("resell_deals", resell, clientId);
        setEngagementDeals(engagement);
        setResellDeals(resell);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading engagement/resell deals:", error);
        setEngagementDeals([]);
        setResellDeals([]);
        setCachedData("engagement_deals", [], clientId);
        setCachedData("resell_deals", [], clientId);
        setLoading(false);
        dataLoadedRef.current.delete(cacheKey);
      });
  }, [clientId, getCachedData, setCachedData]);

  /* ----------------------- PM Stats aggregation --------------------- */

  const handlePmStats = useCallback((dealId, info) => {
    if (!dealId) return;
    setPmStats((prev) => {
      const next = { ...prev };
      next[dealId] = info || {};
      return next;
    });
  }, []);

  /* --------------------------- Derived data --------------------------- */

  const activeEngagementCount = useMemo(() => {
    const total = engagementDeals.length;
    const active = engagementDeals.filter((d) => {
      const s = (d.state || d.status || "").toString().toLowerCase();
      return ["active", "in progress", "in_progress", "operational", "green", "blue", "yellow"].some(
        (k) => s.includes(k)
      );
    }).length;
    return { total, active };
  }, [engagementDeals]);

  const pmUpdatesAggregate = useMemo(() => {
    const stats = Object.values(pmStats);
    const totalUpdates = stats.reduce(
      (sum, s) => sum + (Number.isFinite(s?.totalEntries) ? s.totalEntries : 0),
      0
    );
    const sowsWithUpdates = stats.filter((s) => (s?.totalEntries || 0) > 0).length;
    return { totalUpdates, sowsWithUpdates };
  }, [pmStats]);

  const lastActivity = useMemo(() => {
    const dates = Object.values(pmStats)
      .map((s) => s?.latestDateRaw)
      .filter(Boolean);
    if (!dates.length) return { value: "—", subtitle: "No recent activity" };

    let latest = null;
    let latestDealId = null;
    Object.entries(pmStats).forEach(([dealId, s]) => {
      const raw = s?.latestDateRaw;
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      if (!latest || d > latest) {
        latest = d;
        latestDealId = dealId;
      }
    });

    const deal = engagementDeals.find((d) => String(d.deal_id) === String(latestDealId));
    return {
      value: formatLastActivity(latest),
      subtitle: deal ? `${deal.deal_name}` : "Latest PM update",
    };
  }, [pmStats, engagementDeals]);

  const resellSummary = useMemo(() => {
    const total = resellDeals.length;
    const complete = resellDeals.filter((d) =>
      (d.state || "").toString().toLowerCase().includes("complete")
    ).length;
    return { total, complete };
  }, [resellDeals]);

  /* -------------------------------------------------------------------------- */

  if (!clientId) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "60vh", textAlign: "center" }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(114, 105, 239, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <FaHandshake size={48} color="var(--color-primary)" style={{ opacity: 0.8 }} />
          </div>
          <h3 style={{ color: "var(--color-text)", fontWeight: 700, marginBottom: 12 }}>
            Select a Client
          </h3>
          <p
            style={{
              color: "var(--color-text-light)",
              fontSize: 16,
              maxWidth: 400,
              margin: "0 auto",
            }}
          >
            Please select a client from the search bar above to view their engagement dashboard.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-3 p-md-4" style={{ marginTop: isMobile ? "0.5rem" : "-1.5rem" }}>
      {/* ======================= HEADER ROW ======================= */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: "var(--color-text-light)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Engagements Dashboard
          </div>
          <h2
            style={{
              fontSize: isMobile ? 24 : 30,
              fontWeight: 800,
              color: "var(--color-text)",
              margin: 0,
              lineHeight: 1.15,
            }}
          >
            {clientName || "Engagements"}
          </h2>
          <p
            style={{
              margin: "6px 0 0",
              color: "var(--color-text-light)",
              fontSize: 13.5,
            }}
          >
            Track engagements &amp; resell deal performance
          </p>
        </div>
      </div>

      {/* ======================= SUMMARY CARDS ======================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr 1fr"
            : "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 14,
          marginBottom: 22,
        }}
      >
        <SummaryCard
          eyebrow="Active Engagements"
          value={activeEngagementCount.total}
          accent="#3b82f6"
          subtitle={{
            dot: "#22c55e",
            text:
              activeEngagementCount.total === 0
                ? "No active SOWs"
                : activeEngagementCount.active === activeEngagementCount.total
                ? "Both operational"
                : `${activeEngagementCount.active} operational`,
          }}
        />
        <SummaryCard
          eyebrow="PM Updates"
          value={pmUpdatesAggregate.totalUpdates}
          accent="#a855f7"
          subtitle={{
            text:
              pmUpdatesAggregate.sowsWithUpdates > 0
                ? `Across ${pmUpdatesAggregate.sowsWithUpdates} SOW${
                    pmUpdatesAggregate.sowsWithUpdates === 1 ? "" : "s"
                  }`
                : "No updates yet",
          }}
        />
        <SummaryCard
          eyebrow="Last Activity"
          value={lastActivity.value}
          accent="#f59e0b"
          subtitle={{ text: lastActivity.subtitle }}
        />
        <SummaryCard
          eyebrow="Resell Deals"
          value={resellSummary.total}
          accent="#22c55e"
          subtitle={{
            dot: resellSummary.total > 0 ? "#22c55e" : "#9ca3af",
            text:
              resellSummary.total === 0
                ? "No renewals upcoming"
                : resellSummary.complete > 0
                ? `${resellSummary.complete} complete`
                : `${resellSummary.total} active deal${resellSummary.total === 1 ? "" : "s"}`,
          }}
        />
      </div>

      {/* ============================ TABS ============================ */}
      <div
        className="engagement-tabs-row"
        style={{
          borderBottom: "1px solid var(--color-border)",
          marginBottom: 22,
          display: "flex",
          gap: 6,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {[
          { key: "engagement", label: "Engagements" },
          { key: "resell", label: "Resell deals" },
          { key: "analytics", label: "Analytics" },
        ].map(({ key, label }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              style={{
                position: "relative",
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                fontWeight: active ? 700 : 500,
                color: active ? "var(--color-primary)" : "var(--color-text-light)",
                fontSize: 14,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {label}
              {active && (
                <motion.span
                  layoutId="engagement-tab-underline"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: -1,
                    height: 2,
                    background: "var(--color-primary)",
                    borderRadius: 2,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* ======================= CONTENT ======================= */}
      <AnimatePresence mode="wait">
        {loading && (
          <motion.div key="loading" {...contentAnim} className="text-center py-5">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "3px solid rgba(111,66,193,0.25)",
                borderTopColor: "var(--color-primary)",
                margin: "0 auto",
              }}
            />
            <div className="mt-3" style={{ color: "var(--color-text-light)" }}>
              Loading deals…
            </div>
          </motion.div>
        )}

        {!loading && tab === "engagement" && (
          <motion.div key="engagement" {...contentAnim}>
            {engagementDeals.length ? (
              <EngagementCards deals={engagementDeals} onPmStats={handlePmStats} />
            ) : (
              <EmptyState text="No engagement deals found for this client." />
            )}
          </motion.div>
        )}

        {!loading && tab === "resell" && (
          <motion.div key="resell" {...contentAnim}>
            {resellDeals.length ? (
              <ResellCards deals={resellDeals} />
            ) : (
              <EmptyState text="No resell deals found for this client." />
            )}
          </motion.div>
        )}

        {!loading && tab === "analytics" && (
          <motion.div key="analytics" {...contentAnim}>
            <AnalyticsTab
              engagementDeals={engagementDeals}
              resellDeals={resellDeals}
              pmStats={pmStats}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* --------------------------- Empty State --------------------------- */

const EmptyState = ({ text }) => (
  <div className="text-center py-5" style={{ color: "var(--color-text-light)" }}>
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
      {text}
    </motion.div>
  </div>
);

export default EngagementPage;
